import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, inject, NgZone } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';

declare global {
  interface Window {
    google: typeof google;
  }
}

interface PlaceResult {
  formatted_address?: string;
  formattedAddress?: string;
  place_id?: string;
  [key: string]: any;
}

@Directive({
  selector: '[appPlaceAutocomplete]',
  standalone: true
})
export class PlaceAutocompleteDirective implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private loader = inject(Loader);
  private ngZone = inject(NgZone);
  
  @Input() componentRestrictions?: { country: string | string[] };
  @Output() placeSelected = new EventEmitter<PlaceResult>();

  private autocomplete?: google.maps.places.Autocomplete;
  private static loadPromise: Promise<void> | null = null;
  private static isLoaded = false;

  async ngOnInit(): Promise<void> {
    // Ensure Google Maps is loaded (only once globally)
    await this.loadGoogleMaps();
    
    // Wait for the element to be available
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initializeAutocomplete();
      }, 0);
    });
  }

  private async loadGoogleMaps(): Promise<void> {
    // Use a static promise to ensure we only load once
    if (!PlaceAutocompleteDirective.loadPromise) {
      PlaceAutocompleteDirective.loadPromise = (async () => {
        try {
          // Check if Google Maps is already loaded
          if (window.google?.maps?.places) {
            PlaceAutocompleteDirective.isLoaded = true;
            return;
          }

          // Load Google Maps using the loader with proper async loading
          await this.loader.load();
          
          // Verify Places API is available
          if (!window.google?.maps?.places) {
            throw new Error('Places API not available. Please enable Places API in Google Cloud Console.');
          }
          
          PlaceAutocompleteDirective.isLoaded = true;
        } catch (error) {
          console.error('Error loading Google Maps:', error);
          PlaceAutocompleteDirective.isLoaded = false;
          // Don't throw - allow the component to continue without autocomplete
          return;
        }
      })();
    }

    await PlaceAutocompleteDirective.loadPromise;
  }

  private initializeAutocomplete(): void {
    if (!PlaceAutocompleteDirective.isLoaded || !window.google?.maps?.places) {
      console.error('Google Maps Places API not loaded. Please enable Places API in Google Cloud Console.');
      return;
    }

    const inputElement = this.elementRef.nativeElement as HTMLInputElement;
    
    try {
      // Create Autocomplete options
      const options: google.maps.places.AutocompleteOptions = {
        componentRestrictions: this.componentRestrictions,
        fields: ['formatted_address', 'address_components', 'geometry', 'place_id']
      };

      // Create the Autocomplete instance
      this.autocomplete = new google.maps.places.Autocomplete(inputElement, options);

      // Listen for place selection
      this.autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place = this.autocomplete?.getPlace();
          if (place) {
            // Update the input value
            inputElement.value = place.formatted_address || '';
            
            // Emit the place result with formatted_address
            const result: PlaceResult = {
              formatted_address: place.formatted_address,
              formattedAddress: place.formatted_address,
              place_id: place.place_id,
              ...place
            };
            this.placeSelected.emit(result);
          }
        });
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
      // Don't break the form if autocomplete fails
    }
  }

  ngOnDestroy(): void {
    // Cleanup
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }
}

